/**
 * Assassin Workshop — Cart Drawer lazy bootstrap
 * Lot 8 / v1.2.1
 * Keeps the complete cart drawer and its base CSS off the initial path until cart intent.
 * Submit interception runs after capture-phase safety/attribution guards.
 */
(() => {
  'use strict';

  if (window.AWCartDrawerBootstrap?.version) return;

  const bootstrapScript = document.currentScript;
  let loading = null;
  let coreLoading = null;
  let styleLoading = null;
  let coreLoaded = Boolean(window.AWCartDrawerCore?.version);
  let styleLoaded = false;
  let loaded = false;
  const LOAD_TIMEOUT_MS = 4000;

  const withTimeout = (promise, label) => new Promise((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`${label} timed out`));
    }, LOAD_TIMEOUT_MS);
    promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });

  const assetSrc = (filename) => {
    try {
      const url = new URL(bootstrapScript?.src || '', window.location.href);
      url.pathname = url.pathname.replace(/cart-drawer\.js$/, filename);
      return url.href;
    } catch (_) {
      return '';
    }
  };

  const coreSrc = assetSrc('cart-drawer-core.js');
  const styleSrc = assetSrc('cart-drawer.css');

  const loadStyle = () => {
    if (styleLoaded) return Promise.resolve();
    if (styleLoading) return styleLoading;
    if (!styleSrc) return Promise.reject(new Error('Cart drawer CSS URL unavailable'));

    const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((link) => {
      try { return new URL(link.href, window.location.href).pathname.endsWith('/cart-drawer.css'); }
      catch (_) { return false; }
    });
    if (existing?.sheet) {
      styleLoaded = true;
      return Promise.resolve();
    }

    styleLoading = new Promise((resolve, reject) => {
      const node = existing || document.createElement('link');
      const onLoad = () => {
        styleLoaded = true;
        resolve();
      };
      const onError = () => {
        styleLoading = null;
        reject(new Error('Cart drawer CSS failed to load'));
      };
      node.addEventListener('load', onLoad, { once: true });
      node.addEventListener('error', onError, { once: true });
      if (!existing) {
        node.rel = 'stylesheet';
        node.href = styleSrc;
        node.fetchPriority = 'low';
        node.dataset.awCartDrawerStyle = 'true';
        document.head.appendChild(node);
      }
    });
    return styleLoading;
  };

  const loadCore = () => {
    if (coreLoaded || window.AWCartDrawerCore?.version) {
      coreLoaded = true;
      return Promise.resolve(window.AWCartDrawerCore);
    }
    if (coreLoading) return coreLoading;
    if (!coreSrc) return Promise.reject(new Error('Cart drawer core URL unavailable'));

    coreLoading = new Promise((resolve, reject) => {
      const node = document.createElement('script');
      node.src = coreSrc;
      node.async = true;
      node.dataset.awCartDrawerCore = 'true';
      node.addEventListener('load', () => {
        coreLoaded = Boolean(window.AWCartDrawerCore?.version);
        if (coreLoaded) resolve(window.AWCartDrawerCore);
        else reject(new Error('Cart drawer core did not initialize'));
      }, { once: true });
      node.addEventListener('error', () => {
        coreLoading = null;
        reject(new Error('Cart drawer core failed to load'));
      }, { once: true });
      document.head.appendChild(node);
    });
    return coreLoading;
  };

  const load = () => {
    if (loaded && window.AWCartDrawerCore?.version) return Promise.resolve(window.AWCartDrawerCore);
    if (loading) return loading;

    loading = withTimeout(Promise.all([loadStyle(), loadCore()]), 'Cart drawer bootstrap')
      .then(([, api]) => {
        loaded = true;
        return api;
      })
      .catch((error) => {
        loading = null;
        throw error;
      });
    return loading;
  };

  const isCartLink = (target) => {
    const link = target?.closest?.('a[href*="/cart"]');
    if (!link || link.closest('[data-cart-drawer]') || link.hasAttribute('data-no-drawer')) return null;
    return link;
  };

  const isAddForm = (target) => {
    const form = target?.closest?.('form[action*="/cart/add"]');
    if (!form || form.hasAttribute('data-no-ajax-cart')) return null;
    return form;
  };

  const isCartIntent = (target) => Boolean(
    isCartLink(target) ||
    isAddForm(target) ||
    target?.closest?.('[data-add-button],[data-sticky-button],[data-cart-open]')
  );

  const warm = (event) => {
    if (isCartIntent(event.target)) load().catch(() => {});
  };

  document.addEventListener('pointerover', warm, { capture: true, passive: true });
  document.addEventListener('pointerdown', warm, { capture: true, passive: true });
  document.addEventListener('touchstart', warm, { capture: true, passive: true });
  document.addEventListener('focusin', warm, true);

  document.addEventListener('click', (event) => {
    if (loaded || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = isCartLink(event.target);
    if (!link) return;
    event.preventDefault();
    load()
      .then((api) => {
        if (typeof api?.open === 'function') return api.open();
        window.location.href = link.href;
        return null;
      })
      .catch(() => { window.location.href = link.href; });
  }, true);

  document.addEventListener('submit', (event) => {
    if (loaded || event.defaultPrevented) return;
    const form = isAddForm(event.target);
    if (!form) return;
    event.preventDefault();
    load()
      .then((api) => {
        if (typeof api?.addForm === 'function') return api.addForm(form);
        form.submit();
        return false;
      })
      .catch(() => form.submit());
  });

  document.addEventListener('cart:refresh', () => {
    if (loaded) return;
    load().then((api) => api?.open?.()).catch(() => {});
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted && !loaded) {
      load().then((api) => api?.refresh?.(false)).catch(() => {});
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector?.('[data-cart-drawer]') || event.target.matches?.('[data-cart-drawer]')) {
      load().catch(() => {});
    }
  });

  window.AWCartDrawerBootstrap = {
    version: '1.2.1',
    load,
    get loaded() { return loaded; }
  };

  if (window.Shopify?.designMode) load().catch(() => {});
})();

/* P3 — Pack Upgrade cart grouping: any removal action removes the full logical pack. */
(() => {
  'use strict';
  if (window.AWPackCartGroupRemoveP3?.version) return;

  const VERSION = '1.0.0';
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const packIdOf = (item) => String(item?.properties?._aw_pack_id || '').trim();
  let busy = false;
  let cartCache = null;

  const getCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Panier indisponible');
    return response.json();
  };

  const decorate = (cart) => {
    cartCache = cart?.items ? cart : cartCache;
    const items = cartCache?.items || [];
    const decorateLines = (selector, removeSelector) => {
      document.querySelectorAll(selector).forEach((line, index) => {
        const packId = packIdOf(items[index]);
        if (packId) {
          line.dataset.awPackId = packId;
          const remove = line.querySelector(removeSelector);
          if (remove) {
            remove.textContent = 'Supprimer le pack';
            remove.setAttribute('aria-label', 'Supprimer ce Pack Upgrade du panier');
          }
        } else {
          delete line.dataset.awPackId;
        }
      });
    };
    decorateLines('[data-cart-drawer] [data-cart-line]', '[data-cart-remove]');
    decorateLines('[data-main-cart-section] [data-cart-page-line]', '[data-cart-page-remove]');
  };

  const refresh = async () => {
    try {
      const cart = await getCart();
      decorate(cart);
      return cart;
    } catch (_) {
      return null;
    }
  };

  const removePack = async (packId, fromDrawer) => {
    if (!packId || busy) return;
    busy = true;
    try {
      const cart = await getCart();
      const updates = {};
      (cart.items || []).forEach((item) => {
        if (packIdOf(item) === packId && item.key) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) {
        decorate(cart);
        return;
      }

      const response = await fetch(`${rootUrl()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Suppression du pack impossible');
      const nextCart = await response.json();
      decorate(nextCart);

      if (fromDrawer && typeof window.AWCartDrawerCore?.render === 'function') {
        window.AWCartDrawerCore.render(nextCart);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: nextCart }));
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('[AW Pack cart]', error);
      refresh();
    } finally {
      busy = false;
    }
  };

  const lineFromTarget = (target) => target?.closest?.('[data-cart-line],[data-cart-page-line]');
  const isDrawerLine = (line) => Boolean(line?.closest?.('[data-cart-drawer]'));

  document.addEventListener('click', (event) => {
    const remove = event.target.closest?.('[data-cart-remove],[data-cart-page-remove]');
    const decrease = event.target.closest?.('[data-cart-decrease],[data-cart-page-decrease]');
    if (!remove && !decrease) return;

    const line = lineFromTarget(event.target);
    const packId = String(line?.dataset?.awPackId || '');
    if (!line || !packId) return;

    if (decrease) {
      const input = line.querySelector('[data-cart-quantity],[data-cart-page-quantity]');
      if ((Number(input?.value) || 0) > 1) return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    removePack(packId, isDrawerLine(line));
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-cart-quantity],[data-cart-page-quantity]');
    const line = lineFromTarget(input);
    const packId = String(line?.dataset?.awPackId || '');
    if (!input || !line || !packId || (Number(input.value) || 0) > 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    removePack(packId, isDrawerLine(line));
  }, true);

  document.addEventListener('cart:rendered', (event) => decorate(event.detail));
  document.addEventListener('cart:updated', (event) => decorate(event.detail));
  document.addEventListener('cart:page-updated', (event) => decorate(event.detail?.cart || event.detail));
  document.addEventListener('shopify:section:load', () => refresh());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();

  window.AWPackCartGroupRemoveP3 = { version: VERSION, refresh, decorate, removePack };
})();
